export type AuthProfile = {
  id: string
  username: string
  email: string
  full_name: string
  tutorial_completed: boolean
  is_demo: boolean
  session_token?: string
}

const LOCAL_AUTH_STORAGE_KEY = 'realassist_local_auth_profiles'
const DEMO_PROFILE: AuthProfile = {
  id: 'demo-user-1',
  username: 'demo',
  email: 'demo@realassist.app',
  full_name: 'Demo User',
  tutorial_completed: true,
  is_demo: true,
  session_token: 'demo-local-session',
}

type AuthPayload = {
  username: string
  password: string
}

type SignupPayload = {
  full_name: string
  email: string
  username: string
  password: string
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = await response.json()
    if (typeof body?.detail === 'string') {
      return body.detail
    }
  } catch {
    // no-op
  }
  return `Request failed with ${response.status}`
}

function backendUnavailableError(message: string): boolean {
  return message.includes('Route not implemented') || message.includes('404')
}

function readLocalProfiles(): AuthProfile[] {
  if (typeof window === 'undefined') {
    return [DEMO_PROFILE]
  }
  const raw = window.localStorage.getItem(LOCAL_AUTH_STORAGE_KEY)
  if (!raw) {
    return [DEMO_PROFILE]
  }
  try {
    const parsed = JSON.parse(raw) as AuthProfile[]
    const withoutDemo = parsed.filter((p) => p.username !== DEMO_PROFILE.username)
    return [DEMO_PROFILE, ...withoutDemo]
  } catch {
    return [DEMO_PROFILE]
  }
}

function writeLocalProfiles(profiles: AuthProfile[]) {
  if (typeof window === 'undefined') {
    return
  }
  window.localStorage.setItem(LOCAL_AUTH_STORAGE_KEY, JSON.stringify(profiles))
}

async function postJson<T>(url: string, payload: Record<string, unknown>): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw new Error(await parseError(response))
  }

  return (await response.json()) as T
}

export async function loginAuthProfile(payload: AuthPayload): Promise<AuthProfile> {
  try {
    return await postJson<AuthProfile>('/api/auth/login', payload)
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Auth failed'
    if (!backendUnavailableError(message)) {
      throw error
    }

    const profiles = readLocalProfiles()
    const profile = profiles.find((p) => p.username === payload.username.trim().toLowerCase())
    if (!profile) {
      throw new Error('Invalid username or password.')
    }
    if (profile.username === 'demo' && payload.password === 'demo123') {
      return profile
    }
    const passwordKey = `realassist_local_auth_password_${profile.id}`
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem(passwordKey) : null
    if (!saved || saved !== payload.password) {
      throw new Error('Invalid username or password.')
    }
    return profile
  }
}

export async function signupAuthProfile(payload: SignupPayload): Promise<AuthProfile> {
  try {
    return await postJson<AuthProfile>('/api/auth/signup', payload)
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Signup failed'
    if (!backendUnavailableError(message)) {
      throw error
    }

    const profiles = readLocalProfiles()
    const normalizedUsername = payload.username.trim().toLowerCase()
    const normalizedEmail = payload.email.trim().toLowerCase()

    const duplicate = profiles.find((p) => p.username === normalizedUsername || p.email === normalizedEmail)
    if (duplicate) {
      throw new Error('Username or email is already registered.')
    }

    const profile: AuthProfile = {
      id: `local-${Date.now()}`,
      username: normalizedUsername,
      email: normalizedEmail,
      full_name: payload.full_name.trim(),
      tutorial_completed: false,
      is_demo: false,
    }

    writeLocalProfiles([profile, ...profiles])
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(`realassist_local_auth_password_${profile.id}`, payload.password)
    }
    return profile
  }
}

export async function completeTutorial(userId: string): Promise<AuthProfile> {
  try {
    const token = typeof window !== 'undefined' ? window.localStorage.getItem('realassist_auth_session_token') : null
    const response = await fetch('/api/auth/tutorial/complete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ user_id: userId }),
    })

    if (!response.ok) {
      throw new Error(await parseError(response))
    }

    return (await response.json()) as AuthProfile
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Tutorial completion failed'
    if (!backendUnavailableError(message)) {
      throw error
    }

    const profiles = readLocalProfiles()
    const updatedProfiles = profiles.map((p) => (p.id === userId ? { ...p, tutorial_completed: true } : p))
    writeLocalProfiles(updatedProfiles)
    const profile = updatedProfiles.find((p) => p.id === userId)
    if (!profile) {
      throw new Error('Profile not found.')
    }
    return profile
  }
}
